<template>
  <div class="lang-switch" ref="rootRef">
    <button
      type="button"
      class="lang-trigger"
      @click="isOpen = !isOpen"
      :class="{ open: isOpen }"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="t('common.language')"
    >
      <span class="lang-flag-trigger" aria-hidden="true">{{ getLanguageFlag(currentLocale) }}</span>
      <span class="lang-code">{{ getShortName(currentLocale) }}</span>
      <svg class="lang-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
    </button>

    <Teleport to="body">
      <transition name="lang-drop">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="lang-menu"
          :class="{ 'drop-up': dropUp }"
          :style="menuStyle"
          role="listbox"
        >
          <button
            v-for="(name, code) in availableLocales"
            :key="code"
            class="lang-option"
            :class="{ active: code === currentLocale }"
            @click="selectLanguage(code)"
          >
            <span class="lang-option-code">{{ getShortName(code) }}</span>
            <span v-if="code === currentLocale" class="lang-flag">{{ getLanguageFlag(code) }}</span>
          </button>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { useUserStore } from '@/stores/user'

const props = withDefaults(defineProps<{
  dropUp?: boolean
}>(), {
  dropUp: false
})

const { locale: currentLocale, availableLocales, changeLocale, t } = useI18n()
const userStore = useUserStore()

const isOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const menuStyle = ref<Record<string, string>>({})

const languageFlags: Record<string, string> = {
  ru: '🇷🇺',
  en: '🇬🇧',
  ky: '🇰🇬'
}

const shortNames: Record<string, string> = {
  ru: 'Ru',
  en: 'En',
  ky: 'Ky'
}

function getLanguageFlag(code: string) {
  return languageFlags[code] || '🌐'
}

function getShortName(code: string) {
  return shortNames[code] || code.charAt(0).toUpperCase() + code.slice(1)
}

function updateMenuPosition() {
  if (!rootRef.value) return
  const rect = rootRef.value.getBoundingClientRect()
  const style: Record<string, string> = {
    position: 'fixed',
    right: `${Math.max(8, window.innerWidth - rect.right)}px`,
    zIndex: '400',
    minWidth: `${Math.max(100, rect.width)}px`
  }
  if (props.dropUp) {
    style.bottom = `${window.innerHeight - rect.top + 6}px`
    style.top = 'auto'
  } else {
    style.top = `${rect.bottom + 6}px`
    style.bottom = 'auto'
  }
  menuStyle.value = style
}

async function selectLanguage(code: string) {
  changeLocale(code)
  isOpen.value = false

  if (userStore.isAuthenticated) {
    try {
      await userStore.updateLanguage(code)
    } catch (error) {
      console.error('Error saving language:', error)
    }
  }
}

function handleClickOutside(event: Event) {
  const target = event.target as Node
  if (rootRef.value?.contains(target) || menuRef.value?.contains(target)) return
  isOpen.value = false
}

function handleViewportChange() {
  if (isOpen.value) updateMenuPosition()
}

watch(isOpen, (open) => {
  if (open) updateMenuPosition()
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleViewportChange, true)
  window.addEventListener('resize', handleViewportChange)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleViewportChange, true)
  window.removeEventListener('resize', handleViewportChange)
})
</script>

<style scoped>
.lang-switch {
  position: relative;
}

.lang-flag-trigger {
  font-size: 1.125rem;
  line-height: 1;
}

.lang-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: none;
  border: none;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: background 0.15s ease;
}

.lang-trigger:hover {
  background: var(--background-tertiary);
}

[data-theme="light"] .lang-trigger:hover {
  background: #f1f5f9;
}

.lang-code {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.lang-trigger:hover .lang-code {
  color: var(--text-primary);
}

.lang-chevron {
  color: var(--text-tertiary);
  transition: transform 0.2s ease;
}

.lang-trigger.open .lang-chevron {
  transform: rotate(180deg);
}

@media (max-width: 768px) {
  .lang-trigger {
    padding: 4px 6px;
  }
  .lang-code {
    font-size: 0.875rem;
  }
}
</style>

<style>
/* Teleported menu lives outside the component tree */
.lang-menu {
  background: var(--background-secondary, #1e2329);
  border: 1px solid var(--border-primary, #2b3139);
  border-radius: var(--radius-lg, 12px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  min-width: 100px;
  padding: 4px 0;
}

[data-theme="light"] .lang-menu {
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.lang-menu .lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.12s ease;
  text-align: left;
}

.lang-menu .lang-option:hover {
  background: var(--background-tertiary, #161a1e);
}

[data-theme="light"] .lang-menu .lang-option:hover {
  background: #f1f5f9;
}

.lang-menu .lang-option-code {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-secondary, #848e9c);
}

.lang-menu .lang-option.active .lang-option-code {
  color: var(--text-primary, #eaecf0);
  font-weight: 600;
}

.lang-menu .lang-flag {
  font-size: 1.125rem;
  line-height: 1;
}

.lang-drop-enter-active {
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}
.lang-drop-leave-active {
  transition: all 0.12s ease-in;
}
.lang-drop-enter-from {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}
.lang-drop-leave-to {
  opacity: 0;
  transform: translateY(-3px) scale(0.98);
}
</style>
